import React, { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { SplitContent } from '../SplitContent/SplitContent';
import type { SplitContentMediaPosition, SplitContentProps } from '../SplitContent/SplitContent';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import type { RichTextCtaType } from '../RichText/RichText';
import type { StoreBadgeCtaType } from '../StoreBadgeLink/StoreBadgeLink';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import type { CtaAction } from '../../utils/ctaAction';
import './Tabs.scss';

export const TABS_MIN_COUNT = 2;
export const TABS_MAX_COUNT = 6;

const TAB_SLOTS = [1, 2, 3, 4, 5, 6] as const;
type TabSlot = (typeof TAB_SLOTS)[number];

const DEFAULT_ARIA_LABEL = 'Content sections';

/** CTA styles a tab panel can use (the SplitContent set, without store badges). */
export type TabsCtaType = Exclude<RichTextCtaType, StoreBadgeCtaType>;

/** Flat, per-tab props for slots 1-6. Each tab renders one SplitContent panel. */
export interface TabsTabSlotProps {
  /**
   * Tab 1 label shown in the tab bar and the mobile dropdown.
   * @polyLabel Tab 1 Label
   * @polyHelp Keep it short (1 to 3 words). Required: the block needs at least two tabs.
   */
  tab1Label: string;
  /**
   * Tab 1 image side.
   * @polyType SelectList
   * @polyOptions Left=left,Right=right
   * @polyDefault right
   * @polyLabel Tab 1 Image Side
   * @polyTranslatable false
   */
  tab1MediaPosition?: SplitContentMediaPosition;
  /**
   * Tab 1 image URL.
   * @polyType Url
   * @polyLabel Tab 1 Image URL
   */
  tab1ImageUrl?: string;
  /**
   * Tab 1 image alt text. Leave empty for decorative images.
   * @polyLabel Tab 1 Image Alt Text
   */
  tab1ImageAlt?: string;
  /**
   * Tab 1 eyebrow text above the heading.
   * @polyLabel Tab 1 Eyebrow
   */
  tab1Eyebrow?: string;
  /**
   * Tab 1 body as Markdown: `##` heading, paragraphs, and bulleted or numbered lists.
   * @polyType RichText
   * @polyLabel Tab 1 Content
   */
  tab1Content?: string;
  /**
   * Tab 1 CTA label.
   * @polyLabel Tab 1 CTA Text
   */
  tab1CtaText?: string;
  /**
   * Tab 1 CTA destination.
   * @polyType Url
   * @polyLabel Tab 1 CTA Link
   */
  tab1CtaHref?: string;
  /**
   * Tab 1 CTA style.
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyDefault text-link
   * @polyLabel Tab 1 CTA Type
   * @polyTranslatable false
   */
  tab1CtaType?: TabsCtaType;
  /**
   * Tab 1 CTA tracking ID.
   * @polyLabel Tab 1 CTA ID
   * @polyTranslatable false
   */
  tab1CtaId?: string;
  /**
   * Tab 1 CTA LD appendix.
   * @polyLabel Tab 1 CTA LD Appendix
   * @polyTranslatable false
   */
  tab1CtaLdAppendix?: string;
  /**
   * Tab 1 CTA link behavior.
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Tab 1 CTA Behavior
   * @polyTranslatable false
   */
  tab1CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  tab1CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  tab1CtaTargetPopupId?: string;

  /**
   * Tab 2 label shown in the tab bar and the mobile dropdown.
   * @polyLabel Tab 2 Label
   * @polyHelp Keep it short (1 to 3 words). Required: the block needs at least two tabs.
   */
  tab2Label: string;
  /**
   * Tab 2 image side.
   * @polyType SelectList
   * @polyOptions Left=left,Right=right
   * @polyDefault right
   * @polyLabel Tab 2 Image Side
   * @polyTranslatable false
   */
  tab2MediaPosition?: SplitContentMediaPosition;
  /**
   * Tab 2 image URL.
   * @polyType Url
   * @polyLabel Tab 2 Image URL
   */
  tab2ImageUrl?: string;
  /**
   * Tab 2 image alt text. Leave empty for decorative images.
   * @polyLabel Tab 2 Image Alt Text
   */
  tab2ImageAlt?: string;
  /**
   * Tab 2 eyebrow text above the heading.
   * @polyLabel Tab 2 Eyebrow
   */
  tab2Eyebrow?: string;
  /**
   * Tab 2 body as Markdown: `##` heading, paragraphs, and bulleted or numbered lists.
   * @polyType RichText
   * @polyLabel Tab 2 Content
   */
  tab2Content?: string;
  /**
   * Tab 2 CTA label.
   * @polyLabel Tab 2 CTA Text
   */
  tab2CtaText?: string;
  /**
   * Tab 2 CTA destination.
   * @polyType Url
   * @polyLabel Tab 2 CTA Link
   */
  tab2CtaHref?: string;
  /**
   * Tab 2 CTA style.
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyDefault text-link
   * @polyLabel Tab 2 CTA Type
   * @polyTranslatable false
   */
  tab2CtaType?: TabsCtaType;
  /**
   * Tab 2 CTA tracking ID.
   * @polyLabel Tab 2 CTA ID
   * @polyTranslatable false
   */
  tab2CtaId?: string;
  /**
   * Tab 2 CTA LD appendix.
   * @polyLabel Tab 2 CTA LD Appendix
   * @polyTranslatable false
   */
  tab2CtaLdAppendix?: string;
  /**
   * Tab 2 CTA link behavior.
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Tab 2 CTA Behavior
   * @polyTranslatable false
   */
  tab2CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  tab2CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  tab2CtaTargetPopupId?: string;

  /**
   * Tab 3 label shown in the tab bar and the mobile dropdown.
   * @polyLabel Tab 3 Label
   * @polyHelp Keep it short (1 to 3 words). A tab only appears when its label is set.
   */
  tab3Label?: string;
  /**
   * Tab 3 image side.
   * @polyType SelectList
   * @polyOptions Left=left,Right=right
   * @polyDefault right
   * @polyLabel Tab 3 Image Side
   * @polyTranslatable false
   */
  tab3MediaPosition?: SplitContentMediaPosition;
  /**
   * Tab 3 image URL.
   * @polyType Url
   * @polyLabel Tab 3 Image URL
   */
  tab3ImageUrl?: string;
  /**
   * Tab 3 image alt text. Leave empty for decorative images.
   * @polyLabel Tab 3 Image Alt Text
   */
  tab3ImageAlt?: string;
  /**
   * Tab 3 eyebrow text above the heading.
   * @polyLabel Tab 3 Eyebrow
   */
  tab3Eyebrow?: string;
  /**
   * Tab 3 body as Markdown: `##` heading, paragraphs, and bulleted or numbered lists.
   * @polyType RichText
   * @polyLabel Tab 3 Content
   */
  tab3Content?: string;
  /**
   * Tab 3 CTA label.
   * @polyLabel Tab 3 CTA Text
   */
  tab3CtaText?: string;
  /**
   * Tab 3 CTA destination.
   * @polyType Url
   * @polyLabel Tab 3 CTA Link
   */
  tab3CtaHref?: string;
  /**
   * Tab 3 CTA style.
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyDefault text-link
   * @polyLabel Tab 3 CTA Type
   * @polyTranslatable false
   */
  tab3CtaType?: TabsCtaType;
  /**
   * Tab 3 CTA tracking ID.
   * @polyLabel Tab 3 CTA ID
   * @polyTranslatable false
   */
  tab3CtaId?: string;
  /**
   * Tab 3 CTA LD appendix.
   * @polyLabel Tab 3 CTA LD Appendix
   * @polyTranslatable false
   */
  tab3CtaLdAppendix?: string;
  /**
   * Tab 3 CTA link behavior.
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Tab 3 CTA Behavior
   * @polyTranslatable false
   */
  tab3CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  tab3CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  tab3CtaTargetPopupId?: string;

  /**
   * Tab 4 label shown in the tab bar and the mobile dropdown.
   * @polyLabel Tab 4 Label
   * @polyHelp Keep it short (1 to 3 words). A tab only appears when its label is set.
   */
  tab4Label?: string;
  /**
   * Tab 4 image side.
   * @polyType SelectList
   * @polyOptions Left=left,Right=right
   * @polyDefault right
   * @polyLabel Tab 4 Image Side
   * @polyTranslatable false
   */
  tab4MediaPosition?: SplitContentMediaPosition;
  /**
   * Tab 4 image URL.
   * @polyType Url
   * @polyLabel Tab 4 Image URL
   */
  tab4ImageUrl?: string;
  /**
   * Tab 4 image alt text. Leave empty for decorative images.
   * @polyLabel Tab 4 Image Alt Text
   */
  tab4ImageAlt?: string;
  /**
   * Tab 4 eyebrow text above the heading.
   * @polyLabel Tab 4 Eyebrow
   */
  tab4Eyebrow?: string;
  /**
   * Tab 4 body as Markdown: `##` heading, paragraphs, and bulleted or numbered lists.
   * @polyType RichText
   * @polyLabel Tab 4 Content
   */
  tab4Content?: string;
  /**
   * Tab 4 CTA label.
   * @polyLabel Tab 4 CTA Text
   */
  tab4CtaText?: string;
  /**
   * Tab 4 CTA destination.
   * @polyType Url
   * @polyLabel Tab 4 CTA Link
   */
  tab4CtaHref?: string;
  /**
   * Tab 4 CTA style.
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyDefault text-link
   * @polyLabel Tab 4 CTA Type
   * @polyTranslatable false
   */
  tab4CtaType?: TabsCtaType;
  /**
   * Tab 4 CTA tracking ID.
   * @polyLabel Tab 4 CTA ID
   * @polyTranslatable false
   */
  tab4CtaId?: string;
  /**
   * Tab 4 CTA LD appendix.
   * @polyLabel Tab 4 CTA LD Appendix
   * @polyTranslatable false
   */
  tab4CtaLdAppendix?: string;
  /**
   * Tab 4 CTA link behavior.
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Tab 4 CTA Behavior
   * @polyTranslatable false
   */
  tab4CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  tab4CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  tab4CtaTargetPopupId?: string;

  /**
   * Tab 5 label shown in the tab bar and the mobile dropdown.
   * @polyLabel Tab 5 Label
   * @polyHelp Keep it short (1 to 3 words). A tab only appears when its label is set.
   */
  tab5Label?: string;
  /**
   * Tab 5 image side.
   * @polyType SelectList
   * @polyOptions Left=left,Right=right
   * @polyDefault right
   * @polyLabel Tab 5 Image Side
   * @polyTranslatable false
   */
  tab5MediaPosition?: SplitContentMediaPosition;
  /**
   * Tab 5 image URL.
   * @polyType Url
   * @polyLabel Tab 5 Image URL
   */
  tab5ImageUrl?: string;
  /**
   * Tab 5 image alt text. Leave empty for decorative images.
   * @polyLabel Tab 5 Image Alt Text
   */
  tab5ImageAlt?: string;
  /**
   * Tab 5 eyebrow text above the heading.
   * @polyLabel Tab 5 Eyebrow
   */
  tab5Eyebrow?: string;
  /**
   * Tab 5 body as Markdown: `##` heading, paragraphs, and bulleted or numbered lists.
   * @polyType RichText
   * @polyLabel Tab 5 Content
   */
  tab5Content?: string;
  /**
   * Tab 5 CTA label.
   * @polyLabel Tab 5 CTA Text
   */
  tab5CtaText?: string;
  /**
   * Tab 5 CTA destination.
   * @polyType Url
   * @polyLabel Tab 5 CTA Link
   */
  tab5CtaHref?: string;
  /**
   * Tab 5 CTA style.
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyDefault text-link
   * @polyLabel Tab 5 CTA Type
   * @polyTranslatable false
   */
  tab5CtaType?: TabsCtaType;
  /**
   * Tab 5 CTA tracking ID.
   * @polyLabel Tab 5 CTA ID
   * @polyTranslatable false
   */
  tab5CtaId?: string;
  /**
   * Tab 5 CTA LD appendix.
   * @polyLabel Tab 5 CTA LD Appendix
   * @polyTranslatable false
   */
  tab5CtaLdAppendix?: string;
  /**
   * Tab 5 CTA link behavior.
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Tab 5 CTA Behavior
   * @polyTranslatable false
   */
  tab5CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  tab5CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  tab5CtaTargetPopupId?: string;

  /**
   * Tab 6 label shown in the tab bar and the mobile dropdown.
   * @polyLabel Tab 6 Label
   * @polyHelp Keep it short (1 to 3 words). A tab only appears when its label is set.
   */
  tab6Label?: string;
  /**
   * Tab 6 image side.
   * @polyType SelectList
   * @polyOptions Left=left,Right=right
   * @polyDefault right
   * @polyLabel Tab 6 Image Side
   * @polyTranslatable false
   */
  tab6MediaPosition?: SplitContentMediaPosition;
  /**
   * Tab 6 image URL.
   * @polyType Url
   * @polyLabel Tab 6 Image URL
   */
  tab6ImageUrl?: string;
  /**
   * Tab 6 image alt text. Leave empty for decorative images.
   * @polyLabel Tab 6 Image Alt Text
   */
  tab6ImageAlt?: string;
  /**
   * Tab 6 eyebrow text above the heading.
   * @polyLabel Tab 6 Eyebrow
   */
  tab6Eyebrow?: string;
  /**
   * Tab 6 body as Markdown: `##` heading, paragraphs, and bulleted or numbered lists.
   * @polyType RichText
   * @polyLabel Tab 6 Content
   */
  tab6Content?: string;
  /**
   * Tab 6 CTA label.
   * @polyLabel Tab 6 CTA Text
   */
  tab6CtaText?: string;
  /**
   * Tab 6 CTA destination.
   * @polyType Url
   * @polyLabel Tab 6 CTA Link
   */
  tab6CtaHref?: string;
  /**
   * Tab 6 CTA style.
   * @polyType SelectList
   * @polyOptions Text Link=text-link,Primary Button=primary-button,Secondary Button=secondary-button,Dark Button=dark-button,Anchor Link=anchor-link
   * @polyDefault text-link
   * @polyLabel Tab 6 CTA Type
   * @polyTranslatable false
   */
  tab6CtaType?: TabsCtaType;
  /**
   * Tab 6 CTA tracking ID.
   * @polyLabel Tab 6 CTA ID
   * @polyTranslatable false
   */
  tab6CtaId?: string;
  /**
   * Tab 6 CTA LD appendix.
   * @polyLabel Tab 6 CTA LD Appendix
   * @polyTranslatable false
   */
  tab6CtaLdAppendix?: string;
  /**
   * Tab 6 CTA link behavior.
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Tab 6 CTA Behavior
   * @polyTranslatable false
   */
  tab6CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  tab6CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  tab6CtaTargetPopupId?: string;
}

/** One authored tab, assembled from its flat slot props. */
interface TabItem {
  slot: TabSlot;
  label: string;
  panel: SplitContentProps;
}

function getTab(props: TabsTabSlotProps, slot: TabSlot): TabItem | null {
  const label = props[`tab${slot}Label`]?.trim();
  if (!label) return null;
  return {
    slot,
    label,
    panel: {
      mediaPosition: props[`tab${slot}MediaPosition`],
      imageUrl: props[`tab${slot}ImageUrl`],
      imageAlt: props[`tab${slot}ImageAlt`],
      eyebrow: props[`tab${slot}Eyebrow`],
      content: props[`tab${slot}Content`],
      cta1Text: props[`tab${slot}CtaText`],
      cta1Href: props[`tab${slot}CtaHref`],
      cta1Type: props[`tab${slot}CtaType`] ?? 'text-link',
      cta1Id: props[`tab${slot}CtaId`],
      cta1LdAppendix: props[`tab${slot}CtaLdAppendix`],
      cta1Behavior: props[`tab${slot}CtaBehavior`],
      cta1Action: props[`tab${slot}CtaAction`],
      cta1TargetPopupId: props[`tab${slot}CtaTargetPopupId`],
    },
  };
}

interface PillPosition {
  x: number;
  width: number;
}

/**
 * @polySemanticUsage About this block: a row of 2 to 6 tabs above a SplitContent-style panel
 * (image on one side; eyebrow, heading, body and one CTA on the other). Only the selected tab's
 * panel is visible; the others stay in the page markup for search. Use it when a
 * page presents several parallel options of the same kind (for example programs or tools such
 * as Brand Registry, A+ Content, Vine) and a reader picks one to compare, instead of stacking
 * several SplitContent blocks. Do not use it for sequential steps (use StepsSection) or for
 * questions and answers (use Accordion). Keep tab labels to 1 to 3 words. Fill tab{N} slots in
 * order with no gaps; at least two labelled tabs are required (3 or more is the typical use), and
 * with fewer than two labelled tabs nothing renders. Put the panel heading in
 * tab{N}Content as a `##` heading. Choose tab{N}CtaType by action type: registration actions
 * take primary-button, other primary actions take dark-button, discovery actions (Learn more)
 * take text-link. Always leave tab{N}CtaBehavior unset (same tab).
 */
export interface TabsProps extends TabsTabSlotProps {
  /**
   * Screen-reader name for the tab list and the mobile section picker.
   * @polyLabel Tabs Screen Reader Label
   * @polyHelp Read aloud before the tab names, for example "Selling programs". Not shown on the page.
   */
  ariaLabel?: string;
}

/**
 * Tabs: a segmented tab bar (a dropdown at tablet portrait and below) that switches between
 * SplitContent panels. The selected pill slides to the chosen tab.
 */
export const Tabs: React.FC<TabsProps> = ({ ariaLabel, ...slotProps }) => {
  const tabs = TAB_SLOTS.map((slot) => getTab(slotProps, slot)).filter(
    (tab): tab is TabItem => tab !== null,
  );

  const baseId = useId();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasSwitchedTab, setHasSwitchedTab] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pill, setPill] = useState<PillPosition | null>(null);
  const [isPillAnimated, setIsPillAnimated] = useState(false);
  const [isBarTooWide, setIsBarTooWide] = useState(false);

  const navRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const activeIndex = selectedIndex < tabs.length ? selectedIndex : 0;
  const label = ariaLabel || DEFAULT_ARIA_LABEL;

  // The pill is measured from the selected tab after render. Until then (server render, first
  // paint) the selected tab paints its own white background, so there is no visible jump.
  useEffect(() => {
    const bar = barRef.current;
    const tab = tabRefs.current[activeIndex];
    if (!bar || !tab) return undefined;
    const measure = () => setPill({ x: tab.offsetLeft, width: tab.offsetWidth });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [activeIndex, tabs.length]);

  // Between the dropdown breakpoint and desktop, long labels can make the bar wider than the
  // row. Switch to the dropdown whenever the bar does not fit, instead of clipping a tab.
  useEffect(() => {
    const nav = navRef.current;
    const container = containerRef.current;
    if (!nav || !container) return undefined;
    const check = () => {
      const navStyle = getComputedStyle(nav);
      const padding =
        (parseFloat(navStyle.paddingLeft) || 0) + (parseFloat(navStyle.paddingRight) || 0);
      const available = nav.clientWidth - padding;
      setIsBarTooWide(container.scrollWidth > available);
    };
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(nav);
    if (barRef.current) observer.observe(barRef.current);
    return () => observer.disconnect();
  }, [tabs.length]);

  // Turn the slide transition on only after the pill has its first position, so it does not
  // animate in from the left edge on load.
  useEffect(() => {
    if (!pill || isPillAnimated) return undefined;
    const frame = requestAnimationFrame(() => setIsPillAnimated(true));
    return () => cancelAnimationFrame(frame);
  }, [pill, isPillAnimated]);

  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsDropdownOpen(false);
      triggerRef.current?.focus();
    };
    const handlePointerDown = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isDropdownOpen]);

  if (tabs.length < TABS_MIN_COUNT) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- author guidance during local dev; render aborts here
      console.warn(
        `[Tabs] Needs at least ${TABS_MIN_COUNT} labelled tabs; got ${tabs.length}. ` +
          'Fill tab1Label and tab2Label.',
      );
    }
    return (
      <div
        data-poly-error="tabs-insufficient"
        data-poly-error-detail={`Needs at least ${TABS_MIN_COUNT} labelled tabs, got ${tabs.length}`}
        aria-hidden="true"
        hidden
      />
    );
  }

  const tabId = (index: number) => `${baseId}-tab-${index}`;
  const panelId = (index: number) => `${baseId}-panel-${index}`;
  const listId = `${baseId}-options`;

  const selectTab = (index: number) => {
    setSelectedIndex(index);
    setHasSwitchedTab(true);
  };

  const handleTabKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1;
    const targets: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const next = targets[event.key];
    if (next === undefined) return;
    event.preventDefault();
    selectTab(next);
    tabRefs.current[next]?.focus();
  };

  const handleOptionSelect = (index: number) => {
    selectTab(index);
    setIsDropdownOpen(false);
    triggerRef.current?.focus();
  };

  const pillStyle: CSSProperties | undefined = pill
    ? { width: `${pill.width}px`, transform: `translateX(${pill.x}px)` }
    : undefined;

  const barClassName = [
    'tabs__bar',
    pill ? 'tabs__bar--measured' : '',
    isPillAnimated ? 'tabs__bar--animated' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const sectionClassName = [
    'tabs',
    isBarTooWide ? 'tabs--bar-too-wide' : '',
    hasSwitchedTab ? 'tabs--switched' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={sectionClassName} data-poly-block="Tabs">
      <div className="tabs__nav" ref={navRef}>
        <div className="tabs__container" ref={containerRef}>
          <div className={barClassName} ref={barRef} role="tablist" aria-label={label}>
            <span className="tabs__pill" style={pillStyle} aria-hidden="true" />
            {tabs.map((tab, index) => {
              const isSelected = index === activeIndex;
              return (
                <button
                  key={tab.slot}
                  ref={(node) => {
                    tabRefs.current[index] = node;
                  }}
                  type="button"
                  role="tab"
                  id={tabId(index)}
                  className={`tabs__tab${isSelected ? ' tabs__tab--selected' : ''}`}
                  aria-selected={isSelected}
                  aria-controls={panelId(index)}
                  tabIndex={isSelected ? 0 : -1}
                  onClick={() => selectTab(index)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="tabs__dropdown" ref={dropdownRef}>
          <div className={`tabs__dropdown-card${isDropdownOpen ? ' tabs__dropdown-card--open' : ''}`}>
            <button
              ref={triggerRef}
              type="button"
              className="tabs__dropdown-trigger"
              aria-label={`${label}: ${tabs[activeIndex].label}`}
              aria-expanded={isDropdownOpen}
              aria-controls={listId}
              onClick={() => setIsDropdownOpen((open) => !open)}
            >
              <span className="tabs__dropdown-label">{tabs[activeIndex].label}</span>
              <CustomIcon name="chevron-down" className="tabs__dropdown-chevron" aria-hidden="true" />
            </button>
            <ul id={listId} className="tabs__dropdown-list" hidden={!isDropdownOpen}>
              {tabs.map((tab, index) =>
                index === activeIndex ? null : (
                  <li key={tab.slot}>
                    <button
                      type="button"
                      className="tabs__dropdown-option"
                      onClick={() => handleOptionSelect(index)}
                    >
                      {tab.label}
                    </button>
                  </li>
                ),
              )}
            </ul>
          </div>
        </div>
      </div>

      {tabs.map((tab, index) => (
        <div
          key={tab.slot}
          id={panelId(index)}
          className="tabs__panel"
          role="tabpanel"
          aria-labelledby={tabId(index)}
          tabIndex={0}
          hidden={index !== activeIndex}
        >
          {/* Only the first panel is visible on load, so the other panels' images wait until
              their tab is opened instead of competing with the first image. */}
          <SplitContent {...tab.panel} imageLoading={index === 0 ? undefined : 'lazy'} />
        </div>
      ))}
    </section>
  );
};
